import {
  Callout,
  Code,
  DocPage,
  DocSection,
  K,
  OL,
  P,
  PageFooter,
  PromptCard,
  UL,
} from "./_components";

export function LiveData() {
  return (
    <DocPage
      eyebrow="Building"
      title="Live data, secrets & fetchData"
      intro={
        <>
          Static data is fine for demos and prototypes. Once you want a widget
          backed by a real CRM, billing system, or API, you need two things: a
          way to fetch over HTTPS from the widget, and a way to attach API keys
          without ever putting them in the source. MCPBuild handles both.
        </>
      }
    >
      <DocSection id="why" title="Why a built-in fetch and not just plain JS?">
        <P>
          Widgets run inside an iframe sandboxed to the host's CSP, so
          arbitrary <K>fetch()</K> won't reach most APIs. Plus you can't ship
          an API key client-side without leaking it.
        </P>
        <P>
          MCPBuild's project endpoints expose a built-in <K>fetch</K> tool. The
          widget SDK wraps it as <K>fetchData(url, opts)</K>. The actual HTTP
          request runs server-side, on your project endpoint, with three
          guarantees:
        </P>
        <UL>
          <li>
            Only domains you've allowlisted on the project can be reached. No
            arbitrary egress.
          </li>
          <li>
            Project secrets are stored KMS-encrypted in DynamoDB and injected
            server-side as <K>{`{{secret:NAME}}`}</K> templating in headers,
            URL, or body. The widget JS never sees the key in plaintext.
          </li>
          <li>
            Returns a normalized <K>{`{ status, ok, contentType, body }`}</K>{" "}
            object the widget can render or branch on.
          </li>
        </UL>
      </DocSection>

      <DocSection id="allowlist" title="Set the project's fetch allowlist">
        <P>
          Tell the project which domains its widgets are allowed to fetch from.
          Wildcards (<K>*.example.com</K>) work.
        </P>
        <PromptCard
          title="Allow a GitHub + a public weather API"
          prompt={`Use project set_fetch_allowlist on my "Devboard" project with allowlist ["api.github.com", "api.openweathermap.org"].`}
        />
        <PromptCard
          title="Check what's currently allowed"
          prompt={`Use project get_fetch_config on my "Devboard" project.`}
          note="Returns the allowlist and the names (only the names) of secrets stored on the project."
        />
      </DocSection>

      <DocSection
        id="no-key"
        title="No-code path: live data from a public API (no key)"
      >
        <P>
          The fastest way to see live data — and the one a non-engineer can do
          end to end — uses a public, <strong>unauthenticated</strong> API. No
          secret, no backend: allowlist the domain, point a widget at the URL,
          done. Your <K>Examples</K> project ships with one already, the{" "}
          <K>live-repo-pulse</K> widget, which pulls real star / fork / issue
          counts straight from the public GitHub API.
        </P>
        <PromptCard
          title="Build one in a single prompt"
          prompt={`Create a project called "Repo Pulse".
1. Use project set_fetch_allowlist with allowlist ["api.github.com"].
2. Use dynamic_widget with projectName: "Repo Pulse" and toolSlug: "repo-pulse" — the widget should read { owner, repo } from useToolOutput(), call fetchData("https://api.github.com/repos/<owner>/<repo>", { headers: { "User-Agent": "mcpbuild-widget" } }), and render the repo's stars, forks, and open issues with a Refresh button. Pass data: { owner: "anthropics", repo: "claude-code" }.
3. Return the project's MCP URL.`}
          note={
            <>
              No <K>set_secret</K> step — the GitHub repo endpoint needs no auth.
              The only setup is the one-line allowlist.
            </>
          }
        />
        <Callout kind="note" title="Public APIs are rate-limited per IP">
          Unauthenticated GitHub calls are capped at 60/hour per IP, and the
          server-side fetch shares an IP across callers — fine for a demo, and
          the <K>live-repo-pulse</K> example degrades to a friendly "try again
          shortly" message if it's hit. For anything real, add a token as a
          secret (next section) to lift the limit.
        </Callout>
      </DocSection>

      <DocSection id="secrets" title="Add API keys as project secrets">
        <P>
          Secrets are scoped to one project and KMS-encrypted at rest. You can
          set them via the SPA's Live data panel (preferred for production
          keys) or via a chat prompt (fine for test keys, but the value passes
          through chat).
        </P>
        <PromptCard
          title="Store a secret (chat)"
          prompt={`Use project set_secret on "Devboard" with name OWM_KEY and value <paste-your-test-key>.`}
          note={
            <>
              For real keys, use the project page's Live-data panel instead —
              the value never enters chat.
            </>
          }
        />
        <PromptCard
          title="List / delete secrets"
          prompt={`On "Devboard": project list_secrets, then project delete_secret name OWM_KEY when I tell you to.`}
        />
      </DocSection>

      <DocSection id="fetchData" title="Calling fetchData() from a widget">
        <P>
          Reference a secret in headers, URL, or body with{" "}
          <K>{`{{secret:NAME}}`}</K> — substitution happens server-side.
        </P>
        <Code>{`import { useEffect, useState } from "react";
import { fetchData } from "@martzmakes/widget";

export default function WeatherCard() {
  const [w, setW] = useState(null);
  useEffect(() => {
    (async () => {
      const r = await fetchData(
        "https://api.openweathermap.org/data/2.5/weather?q=Boston&appid={{secret:OWM_KEY}}",
        { method: "GET" }
      );
      if (r.ok) setW(r.body); // server-parsed JSON if contentType is JSON
    })();
  }, []);
  if (!w) return <div>Loading…</div>;
  return <div>{w.name}: {Math.round(w.main.temp - 273.15)}°C, {w.weather[0].description}</div>;
}`}</Code>
        <P>
          <K>fetchData</K> only works once the widget is a project tool — the
          allowlist + secrets live on the project, not on the Inbox. If you're
          previewing on the Inbox, fetchData will fail with "fetch not
          available". Save the widget into a project (<K>projectName</K>) first.
        </P>
      </DocSection>

      <DocSection id="end-to-end" title="End-to-end: a live GitHub-issues widget">
        <PromptCard
          title="One prompt builds the whole project"
          prompt={`Create a project called "Issues".

1. Use project set_fetch_allowlist with allowlist ["api.github.com"].
2. Use project set_secret with name GITHUB_TOKEN — wait, ask me to paste it before sending.
3. Use dynamic_widget with projectName: "Issues" and toolSlug: "open-issues" to render an issues table for one repo. Source should use useToolOutput() to get the owner and repo, then call fetchData("https://api.github.com/repos/<owner>/<repo>/issues?state=open", { headers: { Authorization: "Bearer {{secret:GITHUB_TOKEN}}", "User-Agent": "mcpbuild-widget" } }), and render the list. Pass data: { owner: "anthropic", repo: "anthropic-sdk-python" } as the snapshot.
4. Add triggerPhrases to the tool: ["what's open on anthropic-sdk-python", "show me their open issues", "list the unresolved tickets"].
5. Return the project's MCP URL.`}
          note={
            <>
              When you connect <K>mcpbuild.app/mcp/issues</K> in Claude, saying
              "what's open on anthropic-sdk-python" will trigger the tool — the
              widget renders inline with the live issue list. No client-side
              token, no manual fetch wiring.
            </>
          }
        />
      </DocSection>

      <DocSection id="gotchas" title="Gotchas">
        <OL>
          <li>
            <strong>Inbox widgets can't fetch.</strong> If your widget is in
            Inbox, fetchData fails — there's no project to host the allowlist.
            Use <K>projectName</K> so it's a project tool from the start.
          </li>
          <li>
            <strong>The allowlist is per-project, not per-tool.</strong> Every
            widget in a project can fetch every allowlisted domain.
          </li>
          <li>
            <strong>Secrets are <em>names</em> only in the response.</strong>{" "}
            <K>list_secrets</K> shows names and last-modified, never values.
          </li>
          <li>
            <strong>fetchData returns the body parsed by content-type</strong>{" "}
            — JSON for application/json, string otherwise. Check <K>r.ok</K>{" "}
            before reading the body.
          </li>
        </OL>
      </DocSection>

      <PageFooter
        prev={{ to: "/docs/triggers", label: "Trigger phrases" }}
        next={{ to: "/docs/sharing", label: "Sharing & teams" }}
      />
    </DocPage>
  );
}
